import React, { useMemo } from 'react';
import { TrendingUp, Activity } from 'lucide-react';

export default function PerformanceBreakdown({ analytics, sessions }) {
  const toolStats = useMemo(() => {
    const stats = {
      breath_coach: { count: 0, totalScore: 0, avgScore: 0, label: '🫁 Breath Coach' },
      pitch_coach: { count: 0, totalScore: 0, avgScore: 0, label: '🎵 Pitch Coach' },
      audition_song: { count: 0, totalScore: 0, avgScore: 0, label: '🎤 Audition Song' }
    };

    sessions?.forEach(session => {
      session?.segments?.forEach(segment => {
        if (segment?.performanceScore > 0) {
          stats[segment.toolType].count++;
          stats[segment.toolType].totalScore += segment?.performanceScore;
        }
      });
    });

    Object.keys(stats)?.forEach(tool => {
      if (stats?.[tool]?.count > 0) {
        stats[tool].avgScore = stats?.[tool]?.totalScore / stats?.[tool]?.count;
      }
    });

    return stats;
  }, [sessions]);

  const trendData = useMemo(() => {
    if (sessions?.length === 0) return [];

    const last7Days = sessions?.filter(s => s?.status === 'completed')?.slice(0, 7)?.reverse();

    return last7Days?.map(session => ({
      date: new Date(session.completedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      score: session?.overallScore,
      completion: session?.completionRate
    }));
  }, [sessions]);

  const getScoreColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-blue-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getScoreTextColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 75) return 'text-blue-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const calculateTrend = (toolType) => {
    const recentSessions = sessions?.filter(s => s?.status === 'completed')?.slice(0, 5);

    const toolSegments = recentSessions?.flatMap(s => s?.segments || [])?.filter(seg => seg?.toolType === toolType && seg?.performanceScore > 0);

    if (toolSegments?.length < 2) return 'stable';

    const recent = toolSegments?.slice(0, Math.ceil(toolSegments?.length / 2));
    const older = toolSegments?.slice(Math.ceil(toolSegments?.length / 2));

    const recentAvg = recent?.reduce((sum, s) => sum + s?.performanceScore, 0) / recent?.length;
    const olderAvg = older?.reduce((sum, s) => sum + s?.performanceScore, 0) / older?.length;

    if (recentAvg > olderAvg + 5) return 'improving';
    if (recentAvg < olderAvg - 5) return 'declining';
    return 'stable';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return '📈';
    if (trend === 'declining') return '📉';
    return '➡️';
  };

  const getTrendColor = (trend) => {
    if (trend === 'improving') return 'text-green-400';
    if (trend === 'declining') return 'text-red-400';
    return 'text-white/60';
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <Activity className="w-6 h-6" />
        Performance Breakdown
      </h2>
      {/* Tool-Specific Performance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {Object.entries(toolStats)?.map(([toolType, data]) => {
          const trend = calculateTrend(toolType);
          return (
            <div
              key={toolType}
              className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold text-lg">{data?.label}</h3>
                <span className={getTrendColor(trend)}>
                  {getTrendIcon(trend)}
                </span>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-sm text-white/60 mb-2">
                    <span>Average Score</span>
                    <span className={getScoreTextColor(data?.avgScore)}>
                      {Math.round(data?.avgScore)}%
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className={`h-full rounded-full transition-all ${getScoreColor(data?.avgScore)}`}
                      style={{ width: `${data?.avgScore}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Sessions</span>
                  <span className="text-white font-medium">{data?.count}</span>
                </div>

                <div className={`text-xs ${getTrendColor(trend)} flex items-center gap-1`}>
                  {trend === 'improving' && '↗ Improving trend'}
                  {trend === 'declining' && '↘ Needs attention'}
                  {trend === 'stable' && '→ Consistent performance'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Performance Trend Chart */}
      {trendData?.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Recent Performance Trend
          </h3>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-5">
            <div className="flex items-end justify-between h-48 gap-3">
              {trendData?.map((point, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-xs text-white/60 mb-1">
                    {Math.round(point?.score)}%
                  </div>
                  <div className="w-full bg-white/10 rounded-t-lg relative" style={{ height: '100%' }}>
                    <div
                      className={`absolute bottom-0 w-full rounded-t-lg transition-all ${getScoreColor(point?.score)}`}
                      style={{ height: `${point?.score}%` }}
                    />
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {point?.date}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded" />
                <span className="text-white/60 text-sm">Excellent (90%+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded" />
                <span className="text-white/60 text-sm">Good (75-89%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded" />
                <span className="text-white/60 text-sm">Fair (60-74%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded" />
                <span className="text-white/60 text-sm">Needs Work (&lt;60%)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}